import React, { createContext, useEffect, useLayoutEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

import history from '@/routes/history'

import { redirectIfBannedUser } from './services/auth-redirect'
import { toLegacySubmitting } from './utils/submitting'
import { publicAuthStateDefaults, selectPublicAuthState } from './state/select-public-auth-state'
import { useAuthState } from './hooks/use-auth-state'
import { useAuthSession } from './hooks/use-auth-session'
import { useAuthModal } from './hooks/use-auth-modal'
import { useAuthActions } from './hooks/use-auth-actions'
import { usePendingCredentials } from './hooks/use-pending-credentials'
import { useTurnstileController } from './hooks/use-turnstile'
import { useSessionMonitor } from './hooks/use-session-monitor'
import { useAuthLegacyEvents } from './hooks/use-auth-legacy-events'
import { useActiveSessionsGuard } from './hooks/use-active-sessions-guard'
import { resolveTurnstileStep } from './constants/config'

export const AuthContext = createContext({
  ...publicAuthStateDefaults,
  submitting: false,
})

AuthProvider.propTypes = {
  children: PropTypes.node,
}

export function AuthProvider ({ children }) {
  const {
    user,
    setUser,
    state,
    setState,
    resetState,
    setTurnstileWidgetId,
    isAuth,
    setIsAuth,
  } = useAuthState()

  const pendingCredentials = usePendingCredentials()

  const turnstileStep = resolveTurnstileStep({
    authPageStep: state.authPageStep,
    loginStatus: state.loginStatus,
  })
  const {
    turnstileToken,
    setTurnstileToken,
    mountKey: turnstileMountKey,
    resetKey: turnstileResetKey,
    resetTurnstile,
    destroyTurnstile,
  } = useTurnstileController(setTurnstileWidgetId)

  const session = useAuthSession({
    user,
    setUser,
    state,
    setState,
    resetState,
    setIsAuth,
    clearPendingCredentials: pendingCredentials.clearPendingCredentials,
    destroyTurnstile,
  })

  const modal = useAuthModal({
    state,
    setState,
    clearPendingCredentials: pendingCredentials.clearPendingCredentials,
    resetTurnstile,
  })

  const actions = useAuthActions({
    state,
    setState,
    setUser,
    turnstileToken,
    resetTurnstile,
    closeSessionExpired: session.closeAuthNotice,
    redirectAfterSuccess: session.redirectAfterSuccess,
    pendingCredentials,
  })

  useLayoutEffect(() => {
    session.initialize()
    session.markInitialized()
  }, [state.isAuthenticated])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname === '/' && user && !user.active) {
        redirectIfBannedUser(user)
      }
    })
    return unlisten
  }, [user])

  useAuthLegacyEvents({
    openLoginModal: modal.openLoginModal,
    openSignupModal: modal.openSignupModal,
    closeModal: modal.closeModal,
    logoutExpired: session.logoutExpired,
    logout: session.logout,
  })

  useSessionMonitor(state.isAuthenticated, session.logoutExpired)
  useActiveSessionsGuard(isAuth)

  const value = useMemo(() => ({
    ...selectPublicAuthState(state),
    initialize: session.initialize,
    logout: session.logout,
    user,
    setUser,
    isAuth,
    sendCredentials: actions.sendCredentials,
    responseFacebook: actions.responseFacebook,
    handleModalSessions: actions.handleModalSessions,
    onCloseAllSessionsClicked: actions.onCloseAllSessionsClicked,
    redirectAfterSuccess: session.redirectAfterSuccess,
    closeSessionExpired: session.closeAuthNotice,
    closeAuthNotice: session.closeAuthNotice,
    logoutExpired: session.logoutExpired,
    signupEmail: actions.signupEmail,
    openLoginModal: modal.openLoginModal,
    openSignupModal: modal.openSignupModal,
    openForgotPasswordModal: modal.openForgotPasswordModal,
    closeModal: modal.closeModal,
    setAuthPageStep: modal.setAuthPageStep,
    setAuthNotice: modal.setAuthNotice,
    postFacebookRegister: actions.postFacebookRegister,
    turnstileRuntime: {
      step: turnstileStep,
      mountKey: turnstileMountKey,
      resetKey: turnstileResetKey,
      setToken: setTurnstileToken,
      setWidgetId: setTurnstileWidgetId,
    },
    turnstileToken,
    resetTurnstile,
    submitting: toLegacySubmitting(state.submittingState),
  }), [
    state,
    user,
    setUser,
    isAuth,
    turnstileStep,
    turnstileMountKey,
    turnstileResetKey,
    setTurnstileToken,
    setTurnstileWidgetId,
    turnstileToken,
    resetTurnstile,
    session.initialize,
    session.logout,
    session.redirectAfterSuccess,
    session.closeAuthNotice,
    session.logoutExpired,
    modal.openLoginModal,
    modal.openSignupModal,
    modal.openForgotPasswordModal,
    modal.closeModal,
    modal.setAuthPageStep,
    modal.setAuthNotice,
    actions.sendCredentials,
    actions.responseFacebook,
    actions.handleModalSessions,
    actions.onCloseAllSessionsClicked,
    actions.signupEmail,
    actions.postFacebookRegister,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const AuthContextConsumer = AuthContext.Consumer
